import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, ApiError, UnauthorizedError } from '../../api/client'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../hooks/useToast'
import type { Award } from '../../lib/types'

/**
 * The awards view — new; the prototype had nothing to render here.
 *
 * The prototype counted award blocks and threw them away (it defined an AWARD_SYSTEM prompt
 * it never called), so scanning the CA awards digest produced nothing visible anywhere.
 * Now the backend classifies every winner as partner or competitor intel, keeps the
 * relevant ones, and this view is where that intel lands. The cards reuse the
 * opportunity-card idiom — same expand/collapse, same detail layout — because "who won
 * what" reads the same way as "what can we bid for"; only the classification pill and the
 * outreach angle are award-specific. Filtering stays client-side like the dashboard's stat
 * cards — a few dozen rows don't need server round-trips.
 */

type Filter = 'all' | 'partner' | 'competitor'

function AwardCard({
  award,
  open,
  canDelete,
  onToggle,
  onDelete,
}: {
  award: Award
  open: boolean
  canDelete: boolean
  onToggle: () => void
  onDelete: () => void
}) {
  return (
    <div className={`opp-card ${award.classification}${open ? ' open' : ''}`}>
      <div className="opp-summary" onClick={onToggle}>
        <span className={`award-pill ${award.classification}`}>{award.classification}</span>
        <div className="opp-main">
          <div className="opp-title">{award.title}</div>
          <div className="opp-buyer">
            {award.buyer} · won by <b>{award.winner}</b>
          </div>
        </div>
        <div className="opp-meta">
          {award.value && award.value !== 'TBC' && <span className="opp-value">{award.value}</span>}
          {award.awardDate && <span className="award-date">{award.awardDate}</span>}
        </div>
        <span className="opp-chevron">▶</span>
      </div>
      <div className="opp-detail">
        {award.why && <div className="opp-why">{award.why}</div>}
        {award.outreach && (
          <div className="opp-action-box">
            <div className="opp-action-label">Outreach angle</div>
            <div className="opp-action-text">{award.outreach}</div>
          </div>
        )}
        <div className="opp-btns">
          {award.url && (
            <a href={award.url} target="_blank" rel="noopener noreferrer" className="btn-outline">
              🔗 View award notice
            </a>
          )}
          {/* Curation, not workflow: award digests age fast. Admin-only, like every
              destructive action, and the server enforces it regardless of this flag. */}
          {canDelete && (
            <button className="btn-outline btn-delete" onClick={onDelete}>
              🗑 Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export function AwardsView() {
  const { can } = useAuth()
  const toast = useToast()
  const [params, setParams] = useSearchParams()

  const [awards, setAwards] = useState<Award[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [openCards, setOpenCards] = useState<Set<string>>(new Set())
  // ?scan=<id> shows exactly the award intel one scan saved — the history cup's contract.
  const scanParam = params.get('scan')

  useEffect(() => {
    api
      .awards()
      .then(setAwards)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load award intel.'))
      .finally(() => setLoading(false))
  }, [])

  // Rows saved by the linked scan; empty for scans predating per-scan links, which fall
  // back to everything (with a note) rather than a baffling empty list.
  const scanAwards = useMemo(
    () => (scanParam ? awards.filter((a) => a.scanId === scanParam) : []),
    [awards, scanParam],
  )
  const scanMode = !!scanParam && !loading
  const legacyScan = scanMode && scanAwards.length === 0
  const base = scanMode && !legacyScan ? scanAwards : awards

  const partners = useMemo(() => base.filter((a) => a.classification === 'partner'), [base])
  const competitors = useMemo(() => base.filter((a) => a.classification === 'competitor'), [base])
  // The API already returns saved_at DESC — newest intel first; the filters never reorder.
  const filtered = filter === 'partner' ? partners : filter === 'competitor' ? competitors : base

  function toggleCard(id: string) {
    setOpenCards((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function deleteAward(award: Award) {
    // window.confirm, same reasoning as the dashboard's delete. Nothing cascades from an award.
    if (!window.confirm(`Delete the award record for "${award.title}"?`)) return
    try {
      await api.deleteAward(award.id)
      setAwards((prev) => prev.filter((a) => a.id !== award.id))
      toast('Award deleted')
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        // Another admin beat us to it — reflect reality, don't error.
        setAwards((prev) => prev.filter((a) => a.id !== award.id))
        toast('Already deleted')
      } else if (!(e instanceof UnauthorizedError)) {
        toast(e instanceof Error ? e.message : 'Could not delete the award.')
      }
    }
  }

  const canDelete = can('ADMIN')

  return (
    <div className="view active">
      <div className="dash-header">
        <h1 className="dash-title">Award Intelligence</h1>
        <div className="dash-sub">
          Who won what — award winners from scanned CA emails, classified as partner or competitor intel
        </div>
      </div>

      {scanMode && (
        <div className="scan-subset-note" role="status">
          {legacyScan
            ? 'This scan predates per-scan links, so its exact finds are not recorded — showing all award intel instead.'
            : `Showing only the ${scanAwards.length} award${scanAwards.length === 1 ? '' : 's'} saved by this scan.`}{' '}
          <button onClick={() => setParams({})}>Show all</button>
        </div>
      )}

      <div className="filter-chips">
        <button className={`filter-chip${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>
          All ({base.length})
        </button>
        <button
          className={`filter-chip${filter === 'partner' ? ' active' : ''}`}
          onClick={() => setFilter('partner')}
        >
          Partners ({partners.length})
        </button>
        <button
          className={`filter-chip${filter === 'competitor' ? ' active' : ''}`}
          onClick={() => setFilter('competitor')}
        >
          Competitors ({competitors.length})
        </button>
      </div>

      {error && (
        <div className="err" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="empty">
          <div className="empty-icon">🏆</div>
          {awards.length === 0 ? (
            <>
              <div className="empty-title">No award intel yet</div>
              <div className="empty-sub">Scan a CA email that includes the awards digest.</div>
            </>
          ) : (
            <>
              <div className="empty-title">Nothing under this filter</div>
              <div className="empty-sub">Try a different classification</div>
            </>
          )}
        </div>
      )}

      {filtered.map((a) => (
        <AwardCard
          key={a.id}
          award={a}
          open={openCards.has(a.id)}
          canDelete={canDelete}
          onToggle={() => toggleCard(a.id)}
          onDelete={() => void deleteAward(a)}
        />
      ))}
    </div>
  )
}
